/**
 * stageHistoryRetention — the window iSyn_StageHistory is allowed to cover.
 *
 * WHY THE TABLE NEEDED A LIMIT AT ALL. It is entirely derived — every row is
 * rebuilt from the workbook by any import, and nothing is ever authored there —
 * but it was append-only and unbounded. Past SharePoint's 5,000-item view
 * threshold an unindexed column answers a filter with a bare 500, so first the
 * reads needed a full-table workaround, and by 2026-08-31 the CREATES were
 * being refused outright. The import reported that as "0 stage-history rows
 * synced", which reads as "nothing new", so it went unnoticed for days.
 *
 * Rebuilding the list fixes it once. Capping it is what stops it recurring.
 *
 * The two rules are deliberately separate: a row is only WRITTEN if it is
 * inside the window (writing one that the next run would delete is pure cost),
 * and stale rows are PRUNED in bounded batches (a first run with years of
 * backlog must not turn one import into an hour of deletes).
 */

/** A row's own day, however the caller stores its stamp. */
export interface IRetainable {
  transitionAt: string;
}

/** The oldest day still kept, given a clock and a window. */
export function retentionCutoff(nowMs: number, days: number): string {
  return new Date(nowMs - days * 86400000).toISOString().slice(0, 10);
}

/** Rows inside the window — the only ones worth writing. */
export function withinRetention<T extends IRetainable>(rows: readonly T[], cutoff: string): T[] {
  return rows.filter(r => r.transitionAt.slice(0, 10) >= cutoff);
}

/**
 * Rows that have aged out, capped at `limit`.
 *
 * OLDEST FIRST, so a backlog drains from the far end rather than nibbling at
 * whatever the read happened to return first — otherwise a run-limited prune
 * could revisit the same recent-ish rows for ever and never reach the ones
 * actually holding the table over the threshold.
 */
export function staleRows<T extends IRetainable>(
  rows: readonly T[], cutoff: string, limit: number,
): T[] {
  return rows
    .filter(r => r.transitionAt.slice(0, 10) < cutoff)
    .sort((a, b) => a.transitionAt.localeCompare(b.transitionAt))
    .slice(0, Math.max(0, limit));
}
